import React, { useRef, useEffect, useState } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, AlertTriangle, Check, Target, Award, RotateCcw, Save, Info } from 'lucide-react';
import { NOMBRES_EJES, PLANTILLAS, MAPEO_FACTORES, ADN_ESPECIALIDADES } from './ENARMMatchData';
import { buildExplanationsFromTemplates } from './ENARMMatchLogic';

// =============================================================================
// RADAR CHART COMPONENT (SVG puro, sin dependencias externas)
// =============================================================================
const RadarChart = ({ perfilUsuario, size = 300 }) => {
  const ejes = Object.keys(NOMBRES_EJES);
  const centro = size / 2;
  const radio = size * 0.35;
  const numEjes = ejes.length;
  
  // Calcular puntos del polígono
  const calcularPunto = (index, valor) => {
    const angulo = (Math.PI * 2 * index) / numEjes - Math.PI / 2;
    const r = (valor / 3) * radio; // Normalizado a 0-3
    return {
      x: centro + r * Math.cos(angulo),
      y: centro + r * Math.sin(angulo)
    };
  };

  // Puntos del grid (círculos concéntricos)
  const gridLevels = [1, 2, 3];
  
  // Puntos del perfil del usuario
  const puntosUsuario = ejes.map((eje, i) => calcularPunto(i, perfilUsuario[eje] || 0));
  const pathUsuario = puntosUsuario.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ') + ' Z';

  // Puntos de las etiquetas
  const puntosEtiquetas = ejes.map((eje, i) => {
    const angulo = (Math.PI * 2 * i) / numEjes - Math.PI / 2;
    return {
      x: centro + (radio + 30) * Math.cos(angulo),
      y: centro + (radio + 30) * Math.sin(angulo),
      eje,
      valor: perfilUsuario[eje] || 0
    };
  });

  return (
    <div className="relative">
      <svg width={size} height={size} className="mx-auto">
        {/* Grid circular */}
        {gridLevels.map(level => (
          <circle
            key={level}
            cx={centro}
            cy={centro}
            r={(level / 3) * radio}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        ))}
        
        {/* Líneas radiales */}
        {ejes.map((eje, i) => {
          const punto = calcularPunto(i, 3);
          return (
            <line
              key={eje}
              x1={centro}
              y1={centro}
              x2={punto.x}
              y2={punto.y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}
        
        {/* Área del usuario */}
        <path
          d={pathUsuario}
          fill="rgba(0, 0, 0, 0.1)"
          stroke="black"
          strokeWidth="2"
        />
        
        {/* Puntos en cada vértice */}
        {puntosUsuario.map((punto, i) => (
          <circle
            key={i}
            cx={punto.x}
            cy={punto.y}
            r="4"
            fill="black"
          />
        ))}
        
        {/* Etiquetas de ejes */}
        {puntosEtiquetas.map((item, i) => (
          <text
            key={i}
            x={item.x}
            y={item.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-xs fill-gray-600 font-medium"
          >
            {NOMBRES_EJES[item.eje]}
          </text>
        ))}
      </svg>
    </div>
  );
};

// =============================================================================
// BARRAS DE PERFIL (Accesibilidad)
// =============================================================================
const BarrasPerfil = ({ perfilUsuario }) => {
  const ejes = Object.keys(NOMBRES_EJES);
  
  return (
    <div className="space-y-2">
      {ejes.map(eje => (
        <div key={eje} className="flex items-center space-x-3">
          <span className="text-xs text-gray-600 w-24 text-right">{NOMBRES_EJES[eje]}</span>
          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-black rounded-full transition-all duration-500"
              style={{ width: `${((perfilUsuario[eje] || 0) / 3) * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium w-8">{(perfilUsuario[eje] || 0).toFixed(1)}</span>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// TARJETA DE RECOMENDACIÓN (MEJORADA v2)
// =============================================================================
const TarjetaRecomendacion = ({ especialidad, numero, perfilUsuario, destinoSeleccionado, confianza }) => {
  const [expandido, setExpandido] = useState(false);
  
  const { razones, banderasRojas, advertenciasHardStop } = buildExplanationsFromTemplates(
    especialidad,
    perfilUsuario,
    especialidad.penalizadoresAplicados,
    confianza
  );

  // Generar subespecialidades sugeridas basadas en el destino
  const subsDelTronco = [];
  if (destinoSeleccionado) {
    // Buscar subespecialidades que este tronco habilita
    const especId = especialidad.especialidadId;
    // Simplificación: mostrar el destino si este tronco lo habilita
    if (destinoSeleccionado.troncos_habilitantes?.includes(especId)) {
      subsDelTronco.push(destinoSeleccionado.destino_nombre);
    }
  }

  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center font-bold">
              {numero}
            </div>
            <div>
              <h3 className="font-bold text-black">{especialidad.nombre}</h3>
              <p className="text-xs text-gray-500">{especialidad.abreviatura}</p>
            </div>
          </div>
          <Award size={24} className="text-gray-400" />
        </div>
      </div>

      {/* Contenido */}
      <div className="p-4 space-y-4">
        {/* Razones (cantidad según confianza) */}
        {razones && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">¿Por qué te conviene?</h4>
            <p className="text-sm text-gray-600">{razones}</p>
          </div>
        )}

        {/* Hard Stops advertencias */}
        {advertenciasHardStop && advertenciasHardStop.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {advertenciasHardStop.map((adv, idx) => (
                  <p key={idx} className="text-sm text-red-800">{adv}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Banderas rojas */}
        {banderasRojas.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertTriangle size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {banderasRojas.map((br, idx) => (
                  <p key={idx} className="text-sm text-yellow-800">{br}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Subs sugeridas */}
        {subsDelTronco.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Subespecialidades alineadas</h4>
            <div className="flex flex-wrap gap-2">
              {subsDelTronco.map((sub, idx) => (
                <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  {sub}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Expandir para ver más */}
        <button
          onClick={() => setExpandido(!expandido)}
          className="flex items-center space-x-1 text-sm text-gray-500 hover:text-black transition-colors"
        >
          <span>{expandido ? 'Ver menos' : 'Ver detalle'}</span>
          {expandido ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {expandido && (
          <div className="pt-3 border-t border-gray-200 space-y-4">
            {/* Plan de validación */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Plan de Validación</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start space-x-2">
                  <span className="text-black font-bold">1.</span>
                  <span>Habla con 2 residentes (R2/R3) de esta especialidad</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-black font-bold">2.</span>
                  <span>Haz 1 día de shadowing/rotación observacional</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-black font-bold">3.</span>
                  <span>Revisa guardias y estilo real de vida en tu sede objetivo</span>
                </li>
              </ul>
            </div>

            {/* Contribuciones por eje */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Afinidad por Eje</h4>
              <div className="grid grid-cols-3 gap-2">
                {especialidad.top3Contribuciones.slice(0, 3).map((c, idx) => (
                  <div key={idx} className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500">{c.nombreEje}</div>
                    <div className="text-sm font-bold">{c.contribucion.toFixed(1)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// BLOQUE DE CONFIANZA
// =============================================================================
const BloqueConfianza = ({ confianza }) => {
  const colores = {
    alta: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', badge: 'bg-green-600' },
    media: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', badge: 'bg-yellow-500' },
    baja: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', badge: 'bg-red-600' }
  };
  
  const color = colores[confianza.nivel];

  return (
    <div className={`${color.bg} border ${color.border} rounded-xl p-4 space-y-3`}>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">Confianza del Resultado</h3>
        <span className={`${color.badge} text-white text-sm px-3 py-1 rounded-full font-medium`}>
          {confianza.nivel.charAt(0).toUpperCase() + confianza.nivel.slice(1)}
        </span>
      </div>
      
      <div className="space-y-1">
        {confianza.motivos.map((motivo, idx) => (
          <p key={idx} className={`text-sm ${color.text}`}>{motivo}</p>
        ))}
      </div>
      
      <div className="pt-2 border-t border-gray-200">
        <p className="text-sm font-medium text-gray-700">{confianza.accionSugerida}</p>
      </div>
    </div>
  );
};

// =============================================================================
// COMPONENTE PRINCIPAL DE RESULTADOS (MEJORADO v2)
// =============================================================================
const ENARMMatchResults = ({ resultados, onReiniciar, onGuardar, saving, onBack }) => {
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  
  if (!resultados) return null;

  const {
    perfilUsuario,
    flags,
    top2,
    top5,
    inconsistencias,
    confianza,
    destinoSeleccionado,
    friccionInfo,
    friccionTroncoAlta,
    // Nuevos campos v2
    puntoClave,
    hardStopsActivados,
    exposureLevel
  } = resultados;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          
          <h1 className="text-lg font-bold text-black">Tus Resultados</h1>
          
          <button
            onClick={onGuardar}
            disabled={saving}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            {saving ? (
              <div className="animate-spin h-5 w-5 border-2 border-black border-t-transparent rounded-full" />
            ) : (
              <Save size={20} />
            )}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* NUEVO: Punto Clave (si existe) */}
        {puntoClave && (
          <section className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <Target size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-800 font-medium">{puntoClave}</p>
            </div>
          </section>
        )}

        {/* NUEVO: Hard Stops Activados (si hay) */}
        {hardStopsActivados && hardStopsActivados.length > 0 && (
          <section className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center space-x-2">
              <AlertTriangle size={18} className="text-red-600" />
              <h3 className="font-semibold text-red-800">Filtros de Exclusión Activados</h3>
            </div>
            {hardStopsActivados.map((hs, idx) => (
              <p key={idx} className="text-sm text-red-700 ml-7">{hs.message}</p>
            ))}
          </section>
        )}

        {/* Sección 1: Radar Chart */}
        <section className="space-y-4">
          <h2 className="text-xl font-bold text-black text-center">Tu Perfil</h2>
          <div className="bg-gray-50 rounded-xl p-4">
            <RadarChart perfilUsuario={perfilUsuario} size={280} />
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Valores por Eje (0-3)</h3>
            <BarrasPerfil perfilUsuario={perfilUsuario} />
          </div>
        </section>

        {/* Sección 2: Bloque de Confianza */}
        <section>
          <BloqueConfianza confianza={confianza} />
        </section>

        {/* Sección 3: Top 2 Recomendaciones */}
        <section className="space-y-4">
          <h2 className="text-xl font-bold text-black text-center">Recomendaciones ENARM</h2>
          
          <div className="grid gap-4">
            {top2.map((esp, idx) => (
              <TarjetaRecomendacion
                key={esp.especialidadId}
                especialidad={esp}
                numero={idx + 1}
                perfilUsuario={perfilUsuario}
                destinoSeleccionado={destinoSeleccionado}
                confianza={confianza}
              />
            ))}
          </div>
        </section>

        {/* Botón Ver Detalle */}
        <button
          onClick={() => setMostrarDetalle(!mostrarDetalle)}
          className="w-full py-3 border-2 border-gray-200 rounded-xl text-gray-700 font-medium hover:border-black transition-colors flex items-center justify-center space-x-2"
        >
          <span>{mostrarDetalle ? 'Ocultar detalle' : 'Ver detalle completo'}</span>
          {mostrarDetalle ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {/* Sección 4: Detalle (colapsable) */}
        {mostrarDetalle && (
          <section className="space-y-6 pt-4 border-t border-gray-200">
            {/* Top 5 sin scores */}
            <div>
              <h3 className="text-lg font-semibold text-black mb-3">Top 5 Especialidades</h3>
              <div className="space-y-2">
                {top5.map((esp, idx) => (
                  <div 
                    key={esp.especialidadId}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      idx < 2 ? 'bg-gray-100' : 'bg-gray-50'
                    } ${esp.hardStopBlocked ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <span className="font-medium">{esp.nombre}</span>
                      {esp.hardStopBlocked && (
                        <span className="text-xs text-red-600">(bloqueada)</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">{esp.abreviatura}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Penalizadores aplicados */}
            {top2.some(e => e.penalizadoresAplicados.length > 0) && (
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Penalizadores Aplicados</h3>
                <div className="bg-yellow-50 rounded-lg p-4 space-y-2">
                  {top2.flatMap(e => e.penalizadoresAplicados).map((p, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-sm text-yellow-800">
                      <AlertTriangle size={14} />
                      <span>Baja tolerancia a "{p.factor}" aplicó penalización de {p.penalizacion}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inconsistencias detectadas */}
            {inconsistencias.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Inconsistencias Detectadas</h3>
                <div className="bg-orange-50 rounded-lg p-4 space-y-2">
                  {inconsistencias.map((inc, idx) => (
                    <div key={idx} className="flex items-start space-x-2 text-sm text-orange-800">
                      <Info size={14} className="mt-0.5 flex-shrink-0" />
                      <span>{inc.descripcion}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Destino seleccionado */}
            {destinoSeleccionado && (
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Tu Destino a 10 Años</h3>
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="font-medium text-blue-800">{destinoSeleccionado.destino_nombre}</div>
                  <div className="text-sm text-blue-600 mt-1">{destinoSeleccionado.descripcion_dia_a_dia}</div>
                  {friccionTroncoAlta && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <div className="flex items-center space-x-2 text-sm text-red-600">
                        <AlertTriangle size={14} />
                        <span>Indicaste que NO toleras el tronco necesario</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Nivel de exposición */}
            {exposureLevel && (
              <div>
                <h3 className="text-lg font-semibold text-black mb-3">Tu Nivel de Exposición</h3>
                <div className={`rounded-lg p-4 ${
                  exposureLevel === 'baja' ? 'bg-yellow-50' : 
                  exposureLevel === 'media' ? 'bg-blue-50' : 'bg-green-50'
                }`}>
                  <div className="font-medium">
                    {exposureLevel === 'baja' && 'Poca experiencia real'}
                    {exposureLevel === 'media' && 'Experiencia moderada'}
                    {exposureLevel === 'alta' && 'Amplia experiencia'}
                  </div>
                  {exposureLevel === 'baja' && (
                    <p className="text-sm text-yellow-700 mt-1">
                      Recomendamos validar con shadowing antes de tomar decisiones definitivas.
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Acciones finales */}
        <section className="flex flex-col sm:flex-row gap-3 pt-6">
          <button
            onClick={onGuardar}
            disabled={saving}
            className="flex-1 py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2"
          >
            {saving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <Save size={18} />
                <span>Guardar Resultados</span>
              </>
            )}
          </button>
          
          <button
            onClick={onReiniciar}
            className="flex-1 py-3 border-2 border-gray-200 rounded-xl font-medium hover:border-black transition-colors flex items-center justify-center space-x-2"
          >
            <RotateCcw size={18} />
            <span>Repetir Test</span>
          </button>
        </section>
      </main>
    </div>
  );
};

export default ENARMMatchResults;
